import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import CustomSelect from './CustomSelect';
import './field.scss';

const UA_MONTHS = [
  'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'
];
const isLeap = (y) => (y % 4 === 0 && y % 100 !== 0) || (y % 400 === 0);
const daysInMonth = (y, m) => (!y || !m) ? 31 : (m === 2 ? (isLeap(y) ? 29 : 28) : ([4, 6, 9, 11].includes(m) ? 30 : 31));

const FieldDate = ({
  name,
  value,                 // 'YYYY-MM-DD' або ''
  labelText,
  formClass,
  required,
  minYear = 1930,
  maxYear = new Date().getFullYear(),
  onChange,
}) => {
  const [y, setY] = useState('');
  const [m, setM] = useState('');
  const [d, setD] = useState('');

  useEffect(() => {
    if (!value) { setY(''); setM(''); setD(''); return; }
    const [yy, mm, dd] = String(value).split('-');
    setY(yy ? Number(yy) : '');
    setM(mm ? Number(mm) : '');
    setD(dd ? Number(dd) : '');
  }, [value]);

  const years = useMemo(() => {
    const arr = [];
    for (let yy = maxYear; yy >= minYear; yy--) arr.push({ value: yy, label: String(yy) });
    return arr;
  }, [minYear, maxYear]);

  const months = useMemo(() => UA_MONTHS.map((label, i) => ({ value: i + 1, label })), []);

  const maxDay = daysInMonth(Number(y), Number(m));
  const days = useMemo(
    () => Array.from({ length: maxDay || 31 }, (_, i) => ({ value: i + 1, label: String(i + 1) })),
    [maxDay]
  );

  const classes = ['field', formClass || ''].filter(Boolean).join(' ');

  const emitIfComplete = (yy, mm, dd) => {
    if (yy && mm && dd) {
      const mm2 = String(mm).padStart(2, '0');
      const dd2 = String(dd).padStart(2, '0');
      onChange?.({ target: { value: `${yy}-${mm2}-${dd2}`, name } });
    }
  };

  const handleYear = (val) => {
    const yy = val || '';
    const nextMax = daysInMonth(Number(yy), Number(m));
    const safeD = d && nextMax && d > nextMax ? nextMax : d;
    setY(yy);
    if (safeD !== d) setD(safeD);
    emitIfComplete(yy, m, safeD);
  };
  const handleMonth = (val) => {
    const mm = val || '';
    const nextMax = daysInMonth(Number(y), Number(mm));
    const safeD = d && nextMax && d > nextMax ? nextMax : d;
    setM(mm);
    if (safeD !== d) setD(safeD);
    emitIfComplete(y, mm, safeD);
  };
  const handleDay = (val) => {
    const dd = val || '';
    setD(dd);
    emitIfComplete(y, m, dd);
  };

  return (
    <div className={classes}>
      {labelText && (
        <label className="field__title" htmlFor={`${name}-year`}>
          {labelText}{required ? ' *' : ''}
        </label>
      )}

      <div className="field__date-group cselect-group">
        <CustomSelect
          className="cselect--year"
          name={`${name}-year`}
          options={years}
          value={y}
          onChange={handleYear}
          placeholder="Рік"
          disabled={false}
        />
        <CustomSelect
          className="cselect--month"
          name={`${name}-month`}
          options={months}
          value={m}
          onChange={handleMonth}
          placeholder="Місяць"
          disabled={!y}
        />
        <CustomSelect
          className="cselect--day"
          name={`${name}-day`}
          options={days}
          value={d}
          onChange={handleDay}
          placeholder="День"
          disabled={!y || !m}
        />
      </div>
    </div>
  );
};

FieldDate.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  labelText: PropTypes.string,
  formClass: PropTypes.string,
  required: PropTypes.bool,
  minYear: PropTypes.number,
  maxYear: PropTypes.number,
  onChange: PropTypes.func,
};

export default FieldDate;